import { z } from "zod";
import { ParsePayload } from "zod/v4/core";

import { OperationResult, SpreadsheetRow } from "utils/spreadsheet/types";

/*
 * Schema and type validation logic for bulk assignment spreadsheets.
 *
 * Required fields:
 * - Control_ID: string
 *
 * Optional fields:
 * - Responsible_ID?: number: is mutually exclusive with Responsible_Group_ID
 * - Responsible_Group_ID?: number: is mutually exclusive with Responsible_ID
 * - Default_Claimant_ID?: number
 * - GroupAssignment_Type?: "Group" | "Individual"
 * - Reviewer_ID?: number
 * - Reviewer_Group_ID?: number
 * - SecondReviewer_ID?: number
 * - SecondReviewer_Group_ID?: number
 * - Entity_ID?: number: attaches a control entity to the new assignment
 * - tagColumns?: Record of tag category names to arrays of tag names
 *   Format: "AssigneeTags: CategoryName" columns with semicolon-separated tag values
 *   Example: "AssigneeTags: Location": "Copenhagen, Denmark; Berlin, Germany"
 *
 * Additional metadata fields (optional):
 * - ControlProgramName?: string
 * - ControlProgram_ID?: number
 * - ControlName?: string
 */

const METADATA_IDENTIFIER_COLUMN = "ControlProgramName";
const ASSIGNEE_TAGS_PREFIX = "AssigneeTags: ";

export const FIRST_DATA_ROW = 2;

const SpreadsheetBaseSchema = z
    .looseObject({
        ControlProgramName: z.string().optional(),
        ControlProgram_ID: z.string().optional().transform(Number),
        ControlName: z.string().optional(),
        Control_ID: z.string().min(1, "Control ID is required"),
        Responsible: z.string().optional(),
        Responsible_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Responsible ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),
        Responsible_Group_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Responsible Group ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),
        Default_Claimant_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Default Claimant ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),
        GroupAssignment_Type: z.preprocess(
            (val) => (val === "" ? undefined : val),
            z.enum(["Group", "Individual"]).optional() //NOTE: Group represents Pool and Individual represents Group due to frontend UI terminology
        ),
        Reviewer: z.string().optional(),
        Reviewer_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Reviewer ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),
        Reviewer_Group_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Reviewer Group ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),
        SecondReviewer: z.string().optional(),
        SecondReviewer_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Second Reviewer ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),

        SecondReviewer_Group_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Second Reviewer Group ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),
        Entity: z.string().optional(),
        Entity_ID: z
            .string()
            .refine(
                (val) => val === "" || (!isNaN(Number(val)) && val.length > 0),
                "Entity ID must be a valid number"
            )
            .transform((val) => (val === "" ? undefined : Number(val)))
            .optional(),
    })
    .check((ctx) => {
        const {
            Responsible_ID: RESPONSIBLE_ID,
            Responsible_Group_ID: RESPONSIBLE_GROUP_ID,
            Reviewer_ID: REVIEWER_ID,
            Reviewer_Group_ID: REVIEWER_GROUP_ID,
            SecondReviewer_ID: SECOND_REVIEWER_ID,
            SecondReviewer_Group_ID: SECOND_REVIEWER_GROUP_ID,
            Default_Claimant_ID: DEFAULT_CLAIMANT_ID,
            GroupAssignment_Type: GROUP_ASSIGNMENT_TYPE,
        } = ctx.value;

        const hasReviewer =
            REVIEWER_ID !== undefined || REVIEWER_GROUP_ID !== undefined;
        const hasSecondReviewer =
            SECOND_REVIEWER_ID !== undefined ||
            SECOND_REVIEWER_GROUP_ID !== undefined;

        validateIDPair(
            ctx,
            RESPONSIBLE_ID,
            RESPONSIBLE_GROUP_ID,
            "Responsible ID",
            "Responsible Group ID"
        );
        if (hasReviewer) {
            validateIDPair(
                ctx,
                REVIEWER_ID,
                REVIEWER_GROUP_ID,
                "Reviewer ID",
                "Reviewer Group ID"
            );
        }
        if (hasSecondReviewer) {
            validateIDPair(
                ctx,
                SECOND_REVIEWER_ID,
                SECOND_REVIEWER_GROUP_ID,
                "Second Reviewer ID",
                "Second Reviewer Group ID"
            );
        }

        const hasResponsibleId = RESPONSIBLE_ID !== undefined;
        const hasDefaultClaimantID = DEFAULT_CLAIMANT_ID !== undefined;
        const hasResponsibleGroupID = RESPONSIBLE_GROUP_ID !== undefined;
        const hasGroupAssignmentType = GROUP_ASSIGNMENT_TYPE !== undefined;

        if (hasResponsibleId && hasDefaultClaimantID) {
            ctx.issues.push({
                code: "custom",
                message:
                    "If Responsible ID is provided, Default Claimant ID cannot be provided",
                input: ctx.value,
            });
        }
        if (hasDefaultClaimantID && GROUP_ASSIGNMENT_TYPE !== "Group") {
            ctx.issues.push({
                code: "custom",
                message:
                    "Default Claimant ID can only be set when Group Assignment Type is 'Group'",
                input: ctx.value,
            });
        }

        if (hasResponsibleId && hasGroupAssignmentType) {
            ctx.issues.push({
                code: "custom",
                message:
                    "If Responsible ID is provided, Group Assignment Type cannot be provided",
                input: ctx.value,
            });
        }

        if (hasResponsibleGroupID && !hasGroupAssignmentType) {
            ctx.issues.push({
                code: "custom",
                message:
                    "Group Assignment Type must be set if Responsible Group ID is provided",
                input: ctx.value,
            });
        }
        if (hasSecondReviewer && !hasReviewer) {
            ctx.issues.push({
                code: "custom",
                message:
                    "Second Reviewer cannot be assigned without a Reviewer",
                input: ctx.value,
            });
        }
    });
function validateIDPair(
    ctx: ParsePayload,
    id: number | undefined,
    groupId: number | undefined,
    idLabel: string,
    groupIdLabel: string
) {
    const hasId = id !== undefined;
    const hasGroupId = groupId !== undefined;

    if (!hasId && !hasGroupId) {
        ctx.issues.push({
            code: "custom",
            message: `At least one of ${idLabel} or ${groupIdLabel} must be provided`,
            input: ctx.value,
        });
    }
    if (hasId && hasGroupId) {
        ctx.issues.push({
            code: "custom",
            message: `Both ${idLabel} and ${groupIdLabel} cannot be provided at the same time`,
            input: ctx.value,
        });
    }
}

const validateAssigneeTagColumns = (obj: Record<string, unknown>) => {
    const knownKeys = new Set(Object.keys(SpreadsheetBaseSchema.shape));
    const additionalKeys = Object.keys(obj).filter(
        (key) => !knownKeys.has(key)
    );

    const invalidKeys = additionalKeys.filter(
        (key) => !key.startsWith(ASSIGNEE_TAGS_PREFIX)
    );
    if (invalidKeys.length > 0) {
        throw new Error(`Invalid columns found: ${invalidKeys.join(", ")}`);
    }

    return true;
};

export const SpreadsheetSchema = SpreadsheetBaseSchema.check((ctx) => {
    try {
        validateAssigneeTagColumns(ctx.value);
    } catch (error) {
        ctx.issues.push({
            code: "custom",
            message:
                error instanceof Error
                    ? error.message
                    : "Invalid additional columns",
            input: ctx.value,
        });
    }
});

export type AssignmentRow = z.infer<typeof SpreadsheetSchema> & {
    tagColumns?: Record<string, string[]>;
};

function isValidSpreadsheetRow(row: SpreadsheetRow): boolean {
    const hasData = Object.values(row).some(
        (value) => value !== undefined && value !== "" && value !== null
    );

    const isMetadataRow =
        typeof row[METADATA_IDENTIFIER_COLUMN] === "string" &&
        row.Control_ID === "" &&
        row.Responsible_ID === "";

    return hasData && !isMetadataRow;
}

export function parseSpreadsheetData(
    rows: SpreadsheetRow[]
): OperationResult<AssignmentRow[]> {
    const filteredRows = rows.filter(isValidSpreadsheetRow);

    const errors: string[] = [];
    const validRows: AssignmentRow[] = [];

    filteredRows.forEach((row, index) => {
        const result = SpreadsheetSchema.safeParse(row);
        if (result.success) {
            const processedRow = { ...result.data };
            const tagColumns = extractAssigneeTagColumns(row);

            if (Object.keys(tagColumns).length > 0) {
                processedRow.tagColumns = tagColumns;
            }

            validRows.push(processedRow);
        } else {
            result.error.issues.forEach((error) => {
                const fieldPath = error.path.join(".");
                errors.push(
                    `Row ${index + FIRST_DATA_ROW}: ${fieldPath} - ${
                        error.message
                    }`
                );
            });
        }
    });

    if (errors.length !== 0) {
        return {
            success: false,
            errors,
        };
    }

    return {
        success: true,
        data: validRows,
    };
}

function extractAssigneeTagColumns(
    row: SpreadsheetRow
): Record<string, string[]> {
    const emptyTags: Record<string, string[]> = {};
    return Object.entries(row).reduce((acc, [key, value]) => {
        if (typeof value === "string" && value.trim()) {
            if (key.startsWith(ASSIGNEE_TAGS_PREFIX)) {
                const categoryName = key
                    .replace(ASSIGNEE_TAGS_PREFIX, "")
                    .trim();
                const tagNames = value
                    .split(";")
                    .map((t) => t.trim())
                    .filter(Boolean);

                if (tagNames.length > 0) {
                    acc[categoryName] = tagNames;
                }
            }
        }
        return acc;
    }, emptyTags);
}
